import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { Invoice } from "../../models";
import type { AccountingSettlementStatus } from "../../api";
import {
  getInvoiceStatus,
  type InvoiceStatus,
} from "../../helpers/invoiceFilters";
import { StatusBadge } from "./StatusBadge";

interface InvoiceStatusBadgeProps {
  invoice: Invoice;
  accountingSettlementStatus?: AccountingSettlementStatus | null;
}

const statusStyles: Record<InvoiceStatus, string> = {
  discarded: "bg-surface-muted text-secondary",
  credited:
    "bg-warning-muted dark:bg-warning-muted text-lgb-yellow-400 dark:text-lgb-yellow-400",
  overpaid:
    "bg-warning-muted dark:bg-warning-muted text-lgb-yellow-400 dark:text-lgb-yellow-400",
  paid: "bg-success-muted text-success dark:text-success",
  partially_paid:
    "bg-warning-muted dark:bg-warning-muted text-lgb-yellow-400 dark:text-lgb-yellow-400",
  sent: "bg-success-muted text-success dark:text-success",
  sent_to_accounting:
    "bg-brand-muted dark:bg-brand-muted text-brand dark:text-brand",
  booked: "bg-brand-muted dark:bg-brand-muted text-brand dark:text-brand",
  finalized: "bg-brand-muted dark:bg-brand-muted text-brand dark:text-brand",
  credit_invoice:
    "bg-warning-muted dark:bg-warning-muted text-lgb-yellow-400 dark:text-lgb-yellow-400",
  draft:
    "bg-surface-muted dark:bg-lgb-grey-200 text-secondary dark:text-secondary",
};

export const InvoiceStatusBadge: FC<InvoiceStatusBadgeProps> = ({
  invoice,
  accountingSettlementStatus,
}) => {
  const { t } = useTranslation(["invoice"]);
  const status = getInvoiceStatus(
    invoice,
    accountingSettlementStatus ?? invoice.accountingSettlementStatus,
  );
  const translationKey =
    status === "sent_to_accounting" &&
    invoice.issuanceMode === "accounting_provider"
      ? "invoice:invoice_status.invoice_basis_sent.status"
      : `invoice:invoice_status.${status}.status`;

  return (
    <StatusBadge status={t(translationKey)} bgStyle={statusStyles[status]} />
  );
};
